/**
 * TutorialManager - Component that handles DriverJs integration
 * Renders active tutorial with step navigation and controls
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTutorial } from '../../contexts/TutorialContext';
import { useAuth } from '../../contexts/AuthContext';
import useTutorialTrigger from '../../tutorials/hooks/useTutorialTrigger';
import tutorialService from '../../services/tutorialService';
import logger from '../../utils/logger';

/**
 * Waits for a DOM element matching the given CSS selector to appear.
 * Uses MutationObserver so it works even when the element mounts asynchronously
 * (e.g. after React Query data loads or a panel finishes rendering).
 *
 * @param {string} selector - CSS selector to wait for
 * @param {number} timeout  - Max wait time in ms before rejecting (default 3000)
 * @returns {Promise<void>} Resolves when found, rejects on timeout
 */
const waitForElement = (selector, timeout = 3000) => {
  return new Promise((resolve, reject) => {
    // Already in the DOM — resolve immediately
    if (document.querySelector(selector)) {
      return resolve();
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        observer.disconnect();
        resolve();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Element "${selector}" not found within ${timeout}ms`));
    }, timeout);
  });
};

/**
 * When a step's primary target element is absent, substitute a fallback element
 * and override the step's title/description so the content stays contextually correct.
 *
 * Key:   the primary CSS selector (from the DB step target)
 * Value: { element, title, description } — all optional; only provided fields are overridden
 */
const STEP_FALLBACKS = {
  '[data-tutorial-id="organization-switcher"]': {
    element: '[data-tutorial-id="create-org-button"]',
    title: 'Create Your Organization',
    description:
      'Click here to create an organization and invite your team. ' +
      'Organizations let you share projects, manage members, and collaborate.',
  },
};

// Registry of callback functions that can be triggered by tutorial steps
const tutorialCallbacks = {
  setPostTypeToLink: () => {
    // Find the MUI Select input element
    const selectInput = document.querySelector('#post-type-select');
    
    if (selectInput) {
      // Find React Fiber node to access props and trigger onChange
      const fiberKey = Object.keys(selectInput).find(key => 
        key.startsWith('__reactFiber') || key.startsWith('__reactInternalInstance')
      );
      
      if (fiberKey) {
        const fiber = selectInput[fiberKey];
        const onChange = fiber?.memoizedProps?.onChange || fiber?.pendingProps?.onChange;
        
        if (onChange) {
          // Call onChange with synthetic event
          onChange({ target: { value: 'link' } });
          logger.debug('TutorialManager: Changed post type to link via React Fiber');
          return;
        }
      }
    }
    
    logger.warn('TutorialManager: Could not change post type - element or onChange not found');
  }
};

const TutorialManager = () => {
  const {
    activeTutorial,
    completeTutorial,
    pauseTutorial,
    goToStep
  } = useTutorial();

  const { user } = useAuth();
  const location = useLocation();

  // Initialize tutorial trigger system (handles route-based triggers)
  useTutorialTrigger();

  const [driverInstance, setDriverInstance] = useState(null);
  const [isDriverActive, setIsDriverActive] = useState(false);

  // Ref to reliably track whether the user reached the final step before
  // onDestroyStarted fires. Using a ref (not state) avoids stale closure issues
  // inside the Driver.js callbacks.
  const tourCompletedRef = useRef(false);

  // Define all handler functions first
  const handleComplete = useCallback(() => {
    logger.debug(`TutorialManager: Completing tutorial ${activeTutorial.tutorial_key}`);
    completeTutorial(activeTutorial.tutorial_key, 'completed');
  }, [activeTutorial, completeTutorial]);

  const handleClose = useCallback(() => {
    logger.debug(`TutorialManager: Closing tutorial ${activeTutorial.tutorial_key}`);
    // Don't update progress when closing due to error - tutorial will remain pending
    // and can be retried later
  }, [activeTutorial]);

  // Map all tutorial steps to Driver.js format
  const mapAllStepsToDriver = useCallback(() => {
    if (!activeTutorial || !activeTutorial.steps) return [];

    return activeTutorial.steps.map((step, index) => {
      const isLastStep = index === activeTutorial.steps.length - 1;

      // Apply fallback if the primary target is absent but a fallback element exists
      let resolvedTarget = step.target;
      let resolvedTitle = step.title;
      let resolvedContent = step.content;
      if (step.target && !document.querySelector(step.target)) {
        const fallback = STEP_FALLBACKS[step.target];
        if (fallback && fallback.element && document.querySelector(fallback.element)) {
          resolvedTarget = fallback.element;
          if (fallback.title) resolvedTitle = fallback.title;
          if (fallback.description) resolvedContent = fallback.description;
          logger.debug(
            `TutorialManager: Step ${index} — primary target "${step.target}" absent, ` +
            `using fallback "${resolvedTarget}"`
          );
        }
      }

      const isCenterScreen = !resolvedTarget || resolvedTarget === 'body';

      const stepConfig = {
        popover: {
          title: resolvedTitle,
          description: resolvedContent,
          side: step.position || 'bottom',
          align: isCenterScreen ? 'center' : 'start',
          nextBtnText: isLastStep ? 'Complete' : 'Next',
          prevBtnText: 'Previous',
          doneBtnText: 'Complete'
        }
      };

      // Hide all buttons for steps that expect user action
      if (step.expected_action) {
        stepConfig.popover.showButtons = [];
      }

      // Allow full interaction with the element (including dropdowns/portals)
      if (step.allow_click) {
        stepConfig.popover.disableActiveInteraction = false;
      }

      // Only add element if targeting a specific DOM element.
      // IMPORTANT: step.target must be a valid CSS selector (e.g. "#my-id" or
      // ".my-class"). A plain string like "project-selector" without a leading
      // "#" or "." will not match anything and Driver.js will silently center
      // the step instead of anchoring it to the element.
      if (!isCenterScreen) {
        stepConfig.element = resolvedTarget;
      }

      return stepConfig;
    });
  }, [activeTutorial]);

  // Initialize driver with all steps - let Driver.js handle navigation
  const initializeDriver = useCallback(() => {
    if (!activeTutorial || !activeTutorial.steps) return;

    logger.debug(`TutorialManager: Initializing Driver.js for tutorial ${activeTutorial.tutorial_key}`);

    const allSteps = mapAllStepsToDriver();

    // Reset completion tracking for this tour run
    tourCompletedRef.current = false;

    const driverObj = driver({
      showProgress: true,
      allowClose: false,
      overlayClickNext: false,
      stagePadding: 4,
      stageRadius: 8,
      popoverClass: 'tutorial-popover',
      animate: true,
      smoothScroll: true,
      steps: allSteps,

      onHighlightStarted: (element) => {
        setIsDriverActive(true);

        // Save progress when step changes
        const currentIndex = driverObj.getActiveIndex();
        if (currentIndex !== undefined && currentIndex !== activeTutorial.currentStep) {
          goToStep(currentIndex);
        }

        // Check if element exists - if not, skip this step (for conditional elements)
        const stepConfig = activeTutorial.steps[currentIndex];
        if (!element && stepConfig?.target) {
          logger.warn(`TutorialManager: Step ${currentIndex} target not found: ${stepConfig.target}. Skipping to next step.`);
          setTimeout(() => {
            driverObj.moveNext();
          }, 100);
          return;
        }

        // Execute on_show callback if defined
        if (stepConfig?.on_show && tutorialCallbacks[stepConfig.on_show]) {
          setTimeout(() => {
            tutorialCallbacks[stepConfig.on_show]();
          }, 300); // Small delay to ensure DOM is ready
        }

        // Fix Issue 2: if a parent element has opacity < 1 (common in dark
        // themed apps) the highlighted element will still appear dimmed because
        // it inherits that opacity. Force full opacity on the nearest parent
        // that has an inline opacity style.
        const opaqueParent = element?.closest('[style*="opacity"]');
        if (opaqueParent) {
          opaqueParent.style.setProperty('opacity', '1');
        }
      },

      // Track completion via onNextClick so we have a reliable signal before
      // onDestroyStarted fires. getActiveIndex() inside onDestroyStarted is
      // unreliable because Driver.js may have already advanced or reset its
      // internal index by the time the callback runs.
      onNextClick: () => {
        const currentIndex = driverObj.getActiveIndex();
        const currentStep = activeTutorial.steps[currentIndex];
        
        // Don't allow Next if waiting for expected action
        if (currentStep?.expected_action) {
          return;
        }
        
        if (currentIndex === allSteps.length - 1) {
          tourCompletedRef.current = true;
        }
        driverObj.moveNext();
      },

      onDestroyStarted: () => {
        logger.debug('TutorialManager: Driver.js onDestroyStarted');
        setIsDriverActive(false);

        if (tourCompletedRef.current) {
          handleComplete();
        } else {
          // User skipped or closed before reaching the final step
          handleClose();
        }

        tourCompletedRef.current = false;
      }
    });

    setDriverInstance(driverObj);

    // Wait for the current step's element to be present before starting.
    // This is important when resuming mid-tutorial where the current step
    // might target an element on a hidden tab or async-loaded content.
    const currentStepIndex = activeTutorial.currentStep || 0;
    const currentStepConfig = allSteps[currentStepIndex];
    const startDriver = () => driverObj.drive(currentStepIndex);

    // If the current step has no element (e.g. a center-screen intro), look ahead
    // to the first upcoming step that does require an element and wait for that.
    // This prevents the tour starting before async page content (API-loaded data)
    // has rendered, which would cause all subsequent steps to skip immediately.
    const firstUpcomingElementStep = allSteps
      .slice(currentStepIndex)
      .find(s => s.element);
    const elementToWaitFor = currentStepConfig?.element || firstUpcomingElementStep?.element;

    if (elementToWaitFor) {
      logger.debug(`TutorialManager: Waiting for element before starting: ${elementToWaitFor}`);
      waitForElement(elementToWaitFor, 5000)
        .then(() => {
          logger.debug(`TutorialManager: Element found, starting tutorial`);
          startDriver();
        })
        .catch((err) => {
          logger.error(
            `TutorialManager: Could not start tour — ${err.message}. ` +
            `Will retry in 2 seconds.`
          );
          setTimeout(() => {
            startDriver();
          }, 2000);
        });
    } else {
      // No element targets in any upcoming step — start immediately
      startDriver();
    }
  }, [activeTutorial, mapAllStepsToDriver, handleComplete, handleClose, goToStep]);

  // Event-driven tutorial action listener
  useEffect(() => {
    if (!driverInstance || !activeTutorial) return;

    const handleTutorialAction = async (event) => {
      const { actionId } = event.detail;
      const currentIndex = driverInstance.getActiveIndex();
      const currentStep = activeTutorial.steps[currentIndex];

      logger.debug(`TutorialManager: Action ${actionId} received for step ${currentIndex}`);

      // If current step expects this action, wait for next element then auto-advance
      if (currentStep?.expected_action === actionId) {
        logger.debug(`TutorialManager: Expected action ${actionId} completed, auto-advancing`);
        
        // Get the next step to see if it has a target element
        const nextStepIndex = currentIndex + 1;
        const nextStep = activeTutorial.steps[nextStepIndex];
        
        if (nextStep?.target) {
          try {
            // Wait for the next step's element to appear (e.g., after tab change)
            await waitForElement(nextStep.target, 3000);
          } catch (err) {
            logger.warn(`TutorialManager: Next step element not found: ${err.message}`);
          }
        }

        // Auto-advance after elements are ready
        setTimeout(() => {
          driverInstance.moveNext();
        }, 500);
      }
    };

    window.addEventListener('tutorial-action', handleTutorialAction);
    return () => window.removeEventListener('tutorial-action', handleTutorialAction);
  }, [driverInstance, activeTutorial]);

  // Initialize Driver.js when tutorial becomes active
  useEffect(() => {
    if (activeTutorial && !driverInstance) {
      initializeDriver();
    }

    return () => {
      if (driverInstance) {
        driverInstance.destroy();
        setDriverInstance(null);
        setIsDriverActive(false);
      }
    };
  }, [activeTutorial, driverInstance, initializeDriver]);

  // Pause tutorial when the user navigates away from the tutorial's page
  useEffect(() => {
    if (!activeTutorial || !driverInstance) return;

    const tutorialRouteKey = tutorialService.findTutorialForRoute(location.pathname);
    if (tutorialRouteKey !== activeTutorial.tutorial_key) {
      logger.debug(
        `TutorialManager: Route changed to ${location.pathname}, pausing tutorial ${activeTutorial.tutorial_key}`
      );
      try {
        driverInstance.destroy();
      } catch (_) {}
      setDriverInstance(null);
      setIsDriverActive(false);
      pauseTutorial();
    }
  // Only re-run when the pathname changes — not on every activeTutorial/driverInstance update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Destroy driver when user logs out
  useEffect(() => {
    if (!user && driverInstance && isDriverActive) {
      logger.debug('TutorialManager: User logged out, destroying driver instance');
      try {
        driverInstance.destroy();
      } catch (error) {
        logger.error('TutorialManager: Error destroying driver on logout', error);
      }
      setDriverInstance(null);
      setIsDriverActive(false);
    }
  }, [user, driverInstance, isDriverActive]);

  // DriverJS handles all UI rendering, so we don't need to render anything
  return null;
};

export default TutorialManager;